"use client";

import { useMemo, useState } from "react";
import { SnippetField } from "@/components/embed/snippet-field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildIframeSnippet,
  clampEmbedHeight,
  defaultEmbedAppearance,
  EMBED_ACCENTS,
  EMBED_SITE_URL,
  type EmbedAccent,
  type EmbedAppearance,
  type EmbedChrome,
  type EmbedConfig,
  type EmbedDensity,
  type EmbedKind,
  type EmbedRadius,
  type EmbedTarget,
  type EmbedTheme,
  embedWidgetHref,
} from "@/lib/embed-config";
import { EMBED_COPY, EMBED_PLATFORM_KEYS } from "@/lib/embed-copy";
import type { EmbedTargetOption } from "@/lib/embed-targets";

interface StyleHit {
  slug: string;
  name: string;
  shortName?: string;
}

export function EmbedStudio({
  locale,
  initialTarget,
  cores,
  featuredStyles,
  citationTools,
  writingTools,
}: {
  locale: string;
  initialTarget: EmbedTarget;
  cores: EmbedTargetOption[];
  featuredStyles: EmbedTargetOption[];
  citationTools: EmbedTargetOption[];
  writingTools: EmbedTargetOption[];
}) {
  const [kind, setKind] = useState<EmbedKind>(initialTarget.kind);
  const [slug, setSlug] = useState(initialTarget.slug ?? "");
  const [appearance, setAppearance] = useState<EmbedAppearance>(
    defaultEmbedAppearance()
  );
  const [source, setSource] = useState("");
  const [styleQuery, setStyleQuery] = useState("");
  const [styleHits, setStyleHits] = useState<StyleHit[]>([]);
  const [toolQuery, setToolQuery] = useState("");

  const target = useMemo(
    () => resolveDraftTarget(kind, slug, featuredStyles, citationTools),
    [kind, slug, featuredStyles, citationTools]
  );

  const origin =
    typeof window === "undefined" ? EMBED_SITE_URL : window.location.origin;
  const config: EmbedConfig = {
    locale,
    target,
    appearance,
    source: source.trim() || undefined,
  };
  const title = titleFor(
    target,
    featuredStyles,
    citationTools,
    writingTools,
    cores
  );
  const pagePath = pagePathFor(
    target,
    featuredStyles,
    citationTools,
    writingTools,
    cores
  );
  const snippet = buildIframeSnippet(config, { title, origin, pagePath });
  const widgetHref = embedWidgetHref(config, origin);
  const oembedUrl = `${origin}/api/oembed?url=${encodeURIComponent(widgetHref)}`;

  const tools = [...citationTools, ...writingTools];
  const visibleTools = filterOptions(tools, toolQuery);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
      <div className="space-y-8">
        <section className="space-y-4">
          <h2 className="font-semibold text-lg">
            {EMBED_COPY.studio.targetHeading}
          </h2>
          <KindTabs kind={kind} onChange={setKind} />
          {kind === "style" ? (
            <StylePicker
              featured={featuredStyles}
              hits={styleHits}
              onQuery={async (query) => {
                setStyleQuery(query);
                setStyleHits(await searchStyles(query));
              }}
              onSelect={setSlug}
              query={styleQuery}
              selected={slug}
            />
          ) : null}
          {kind === "tool" ? (
            <ToolPicker
              onQuery={setToolQuery}
              onSelect={setSlug}
              options={visibleTools}
              query={toolQuery}
              selected={slug}
            />
          ) : null}
          {kind === "generator" || kind === "bibliography" ? (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {cores.find((item) => item.kind === kind)?.description}
            </p>
          ) : null}
        </section>

        <AppearanceForm appearance={appearance} onChange={setAppearance} />

        <section className="space-y-2">
          <Label htmlFor="embed-source">{EMBED_COPY.studio.prefillLabel}</Label>
          <Input
            id="embed-source"
            onChange={(event) => {
              setSource(event.target.value);
            }}
            placeholder={EMBED_COPY.studio.prefillPlaceholder}
            value={source}
          />
        </section>

        <Button
          onClick={() => {
            setAppearance(defaultEmbedAppearance());
            setSource("");
          }}
          type="button"
          variant="ghost"
        >
          {EMBED_COPY.studio.reset}
        </Button>
      </div>

      <div className="space-y-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-lg">
              {EMBED_COPY.studio.previewHeading}
            </h2>
            <a
              className="text-primary text-sm underline-offset-4 hover:underline"
              href={widgetHref}
              rel="noreferrer"
              target="_blank"
            >
              {EMBED_COPY.studio.openFullPage}
            </a>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-muted/20">
            <iframe
              className="w-full bg-background"
              height={appearance.height}
              src={widgetHref}
              style={{ border: 0, maxWidth: "100%" }}
              title={title}
            />
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="font-semibold text-lg">
            {EMBED_COPY.studio.snippetHeading}
          </h2>
          <SnippetField
            id="embed-html"
            label={EMBED_COPY.studio.htmlLabel}
            value={snippet}
          />
          <SnippetField
            id="embed-oembed"
            label={EMBED_COPY.studio.oembedLabel}
            rows={2}
            value={oembedUrl}
          />
          <p className="text-muted-foreground text-xs leading-relaxed">
            {EMBED_COPY.studio.scriptNote}
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="font-semibold text-lg">
            {EMBED_COPY.studio.platformsHeading}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {EMBED_PLATFORM_KEYS.map((key) => {
              const platform = EMBED_COPY.platforms[key];
              return (
                <article
                  className="rounded-xl border border-border bg-card p-4"
                  key={key}
                >
                  <h3 className="font-medium text-sm">{platform.name}</h3>
                  <p className="mt-2 text-muted-foreground text-xs leading-relaxed">
                    {platform.steps}
                  </p>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function resolveDraftTarget(
  kind: EmbedKind,
  slug: string,
  styles: EmbedTargetOption[],
  tools: EmbedTargetOption[]
): EmbedTarget {
  if (kind === "generator" || kind === "bibliography") {
    return { kind };
  }
  if (slug) {
    return { kind, slug };
  }
  const fallback = kind === "style" ? styles[0]?.slug : tools[0]?.slug;
  return fallback ? { kind, slug: fallback } : { kind: "generator" };
}

function titleFor(
  target: EmbedTarget,
  styles: EmbedTargetOption[],
  tools: EmbedTargetOption[],
  writing: EmbedTargetOption[],
  cores: EmbedTargetOption[]
): string {
  if (target.kind === "generator" || target.kind === "bibliography") {
    return (
      cores.find((item) => item.kind === target.kind)?.title ?? "CitationEasy"
    );
  }
  const options = target.kind === "style" ? styles : [...tools, ...writing];
  return (
    options.find((item) => item.slug === target.slug)?.title ?? "CitationEasy"
  );
}

/**
 * Canonical page for the selected target, when the studio knows one.
 *
 * `titleFor`'s shape, for the same reason: the featured lists carry the real
 * `pagePath`, so a landing style credits `/apa` instead of the equally valid but
 * uglier `/citation-style/apa` the structural fallback would produce. A style found
 * through search is not in these lists and falls back, which is correct.
 */
function pagePathFor(
  target: EmbedTarget,
  styles: EmbedTargetOption[],
  tools: EmbedTargetOption[],
  writing: EmbedTargetOption[],
  cores: EmbedTargetOption[]
): string | undefined {
  if (target.kind === "generator" || target.kind === "bibliography") {
    return cores.find((item) => item.kind === target.kind)?.pagePath;
  }
  const options = target.kind === "style" ? styles : [...tools, ...writing];
  return options.find((item) => item.slug === target.slug)?.pagePath;
}

function filterOptions(options: EmbedTargetOption[], query: string) {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return options;
  }
  return options.filter((option) => {
    const haystack =
      `${option.title} ${option.slug ?? ""} ${option.description}`.toLowerCase();
    return haystack.includes(needle);
  });
}

async function searchStyles(query: string): Promise<StyleHit[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) {
    return [];
  }
  const response = await fetch(
    `/api/search-styles?q=${encodeURIComponent(trimmed)}`
  );
  if (!response.ok) {
    return [];
  }
  const data = (await response.json()) as { results?: StyleHit[] };
  return data.results ?? [];
}

function KindTabs({
  kind,
  onChange,
}: {
  kind: EmbedKind;
  onChange: (kind: EmbedKind) => void;
}) {
  const tabs: { id: EmbedKind; label: string }[] = [
    { id: "generator", label: EMBED_COPY.studio.kindGenerator },
    { id: "style", label: EMBED_COPY.studio.kindStyle },
    { id: "tool", label: EMBED_COPY.studio.kindTool },
    { id: "bibliography", label: EMBED_COPY.studio.kindBibliography },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {tabs.map((tab) => (
        <Button
          key={tab.id}
          onClick={() => {
            onChange(tab.id);
          }}
          type="button"
          variant={kind === tab.id ? "default" : "outline"}
        >
          {tab.label}
        </Button>
      ))}
    </div>
  );
}

function StylePicker({
  featured,
  hits,
  query,
  selected,
  onQuery,
  onSelect,
}: {
  featured: EmbedTargetOption[];
  hits: StyleHit[];
  query: string;
  selected: string;
  onQuery: (query: string) => void;
  onSelect: (slug: string) => void;
}) {
  const options = hits.length > 0 ? hits : featured;
  return (
    <div className="space-y-3">
      <Label htmlFor="embed-style-search">
        {EMBED_COPY.studio.styleSearchLabel}
      </Label>
      <Input
        id="embed-style-search"
        onChange={(event) => {
          onQuery(event.target.value);
        }}
        placeholder={EMBED_COPY.studio.styleSearchPlaceholder}
        value={query}
      />
      <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-1">
        {options.map((option) => {
          const optionSlug = option.slug;
          if (!optionSlug) {
            return null;
          }
          return (
            <li key={optionSlug}>
              <button
                className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                  selected === optionSlug
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent"
                }`}
                onClick={() => {
                  onSelect(optionSlug);
                }}
                type="button"
              >
                {styleOptionLabel(option)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ToolPicker({
  options,
  query,
  selected,
  onQuery,
  onSelect,
}: {
  options: EmbedTargetOption[];
  query: string;
  selected: string;
  onQuery: (query: string) => void;
  onSelect: (slug: string) => void;
}) {
  return (
    <div className="space-y-3">
      <Label htmlFor="embed-tool-search">
        {EMBED_COPY.studio.toolSearchLabel}
      </Label>
      <Input
        id="embed-tool-search"
        onChange={(event) => {
          onQuery(event.target.value);
        }}
        placeholder={EMBED_COPY.studio.toolSearchPlaceholder}
        value={query}
      />
      <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-border p-1">
        {options.map((option) => (
          <li key={option.slug}>
            <button
              className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                selected === option.slug
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent"
              }`}
              onClick={() => {
                if (option.slug) {
                  onSelect(option.slug);
                }
              }}
              type="button"
            >
              {option.title}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AppearanceForm({
  appearance,
  onChange,
}: {
  appearance: EmbedAppearance;
  onChange: (next: EmbedAppearance) => void;
}) {
  function patch(partial: Partial<EmbedAppearance>) {
    onChange({ ...appearance, ...partial });
  }

  return (
    <section className="space-y-4">
      <h2 className="font-semibold text-lg">
        {EMBED_COPY.studio.appearanceHeading}
      </h2>
      <SelectField
        label={EMBED_COPY.studio.themeLabel}
        onChange={(value) => {
          patch({ theme: value as EmbedTheme });
        }}
        options={[
          ["system", EMBED_COPY.studio.themeSystem],
          ["light", EMBED_COPY.studio.themeLight],
          ["dark", EMBED_COPY.studio.themeDark],
        ]}
        value={appearance.theme}
      />
      <SelectField
        label={EMBED_COPY.studio.chromeLabel}
        onChange={(value) => {
          patch({ chrome: value as EmbedChrome });
        }}
        options={[
          ["branded", EMBED_COPY.studio.chromeBranded],
          ["minimal", EMBED_COPY.studio.chromeMinimal],
          ["bare", EMBED_COPY.studio.chromeBare],
        ]}
        value={appearance.chrome}
      />
      <SelectField
        label={EMBED_COPY.studio.densityLabel}
        onChange={(value) => {
          patch({ density: value as EmbedDensity });
        }}
        options={[
          ["compact", EMBED_COPY.studio.densityCompact],
          ["comfortable", EMBED_COPY.studio.densityComfortable],
          ["spacious", EMBED_COPY.studio.densitySpacious],
        ]}
        value={appearance.density}
      />
      <SelectField
        label={EMBED_COPY.studio.radiusLabel}
        onChange={(value) => {
          patch({ radius: value as EmbedRadius });
        }}
        options={["none", "sm", "md", "lg", "xl"].map((value) => [
          value,
          value,
        ])}
        value={appearance.radius}
      />
      <SelectField
        label={EMBED_COPY.studio.accentLabel}
        onChange={(value) => {
          patch({ accent: value as EmbedAccent });
        }}
        options={EMBED_ACCENTS.map((value) => [value, value])}
        value={appearance.accent}
      />
      <div className="space-y-1.5">
        <Label htmlFor="embed-height">{EMBED_COPY.studio.heightLabel}</Label>
        <Input
          id="embed-height"
          max={2400}
          min={360}
          onChange={(event) => {
            patch({ height: clampEmbedHeight(Number(event.target.value)) });
          }}
          type="number"
          value={appearance.height}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={appearance.showTitle}
          onChange={(event) => {
            patch({ showTitle: event.target.checked });
          }}
          type="checkbox"
        />
        {EMBED_COPY.studio.showTitle}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={appearance.showFooter}
          onChange={(event) => {
            patch({ showFooter: event.target.checked });
          }}
          type="checkbox"
        />
        {EMBED_COPY.studio.showFooter}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={appearance.showBorder}
          onChange={(event) => {
            patch({ showBorder: event.target.checked });
          }}
          type="checkbox"
        />
        {EMBED_COPY.studio.showBorder}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={appearance.autoResize}
          onChange={(event) => {
            patch({ autoResize: event.target.checked });
          }}
          type="checkbox"
        />
        {EMBED_COPY.studio.autoResize}
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={appearance.attribution}
          onChange={(event) => {
            patch({ attribution: event.target.checked });
          }}
          type="checkbox"
        />
        {EMBED_COPY.studio.showAttribution}
      </label>
      <p className="text-muted-foreground text-xs leading-relaxed">
        {EMBED_COPY.studio.attributionNote}
      </p>
    </section>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: (readonly [string, string])[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select onValueChange={onChange} value={value}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([id, optionLabel]) => (
            <SelectItem key={id} value={id}>
              {optionLabel}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function styleOptionLabel(option: EmbedTargetOption | StyleHit): string {
  if ("title" in option && option.title) {
    return option.title;
  }
  if ("shortName" in option && option.shortName) {
    return option.shortName;
  }
  if ("name" in option && option.name) {
    return option.name;
  }
  return option.slug ?? "Style";
}
